import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";

import {
  useGetSupportStatusesQuery,
  useLazyGetSupportTicketsQuery,
} from "../../../api/support";
import AlertBottom from "../../common/alertBottom";
import Preloader from "../../common/preloader";
import SupportEmptyRequests from "./components/empty-requests";
import SupportGuidelines from "./components/guidelines";
import SupportRequestRequirements from "./components/request-requirements";
import SupportRequestsList from "./components/requests-list";
import {useSupportTickets} from "./support-hooks";
import SupportPageLayout from "./support-page-layout";

const INITIAL_SUPPORT_TICKETS_REQUEST: SupportTicketsRequest = {
  limit: 10,
  ordering: '-created_at',
};

const SupportPage = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const tickets = useSupportTickets();

  const locationState = location.state as SupportRequestLocationState | null;

  useGetSupportStatusesQuery();
  const [ supportTicketsRequest, {isLoading: isLoadingTickets} ] = useLazyGetSupportTicketsQuery();

  const [ hasLoadedFirstRequest, setHasLoadedFirstRequest ] = useState(false);
  const [ hasTickets, setHasTickets ] = useState(false);

  const [ isAlertOpen, setIsAlertOpen ] = useState(Boolean(locationState?.requestCreated));

  const ticketsParams = tickets?.params;

  useEffect(() => {
    supportTicketsRequest(INITIAL_SUPPORT_TICKETS_REQUEST, false).then(({data}) => {
      setHasTickets(Boolean(data?.count));
      setHasLoadedFirstRequest(true);
    });
  }, [ supportTicketsRequest ]);

  const handleCreateRequest = () => {
    navigate('/cabinet/support/new');
  }

  const handleSupportTicketsRequest = (params?: DictionaryParamsInput) => {
    if (!ticketsParams) {
      return;
    }

    supportTicketsRequest({
      ...ticketsParams,
      ...params,
    });
  }

  const handleSearch = (search: string) => {
    handleSupportTicketsRequest({
      page: 1,
      search: search || undefined,
    });
  }

  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    navigate('/cabinet/support', {replace: true, state: null});
  }

  const isLoading = isLoadingTickets && !hasLoadedFirstRequest;

  if (isLoading) {
    return (
      <SupportPageLayout>
        <Preloader />
      </SupportPageLayout>
    );
  }

  return (
    <>
      <SupportPageLayout>
        {hasTickets ? (
          <SupportRequestsList
            handleSupportTicketsRequest={handleSupportTicketsRequest}
            onSearch={handleSearch}
          />
        ) : (
          <SupportEmptyRequests onCreateRequest={handleCreateRequest} />
        )}
        <SupportGuidelines />
        <SupportRequestRequirements />
      </SupportPageLayout>
      <AlertBottom
        open={isAlertOpen}
        message={t('support__message__requestCreated')}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default SupportPage;
